import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AccountScoringEmbedModal } from "@features/AccountScoringEmbedModal/AccountScoringEmbedModal";
import { useBudgetFunctionalityDisabledError } from "@features/Budget/hooks/useBudgetFunctionalityDisabledError";
import { BankButton } from "@components/BankButton/BankButton";
import { BankButtonSkeleton } from "@components/BankButton/BankButtonSkeleton";
import { Button } from "@components/Button";
import { ExpandingContainer } from "@components/ExpandingContainer/ExpandingContainer";
import { parseHtml } from "@components/HTMLParser/utils";
import { PlusIcon } from "@components/icons";
import { FormCheckbox } from "@components/inputs/form/FormCheckbox/FormCheckbox";
import { Link } from "@components/Link/Link";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { useInitiateConsentMutation } from "@services/budget/mutations";
import { useGetAvailableBanksQuery } from "@services/budget/queries";
import {
  useAddAccountSchema,
  type TAddAccountModalSchema,
} from "./useAddAccountSchema";

interface IAddBankAccountModalProps extends IUseModalProps {
  preselectedBankId?: number;
}

export const AddBankAccountModal: React.FC<IAddBankAccountModalProps> = (
  props,
) => {
  const { preselectedBankId, ...rest } = props;
  const modal = useModal(rest);
  const { create } = useModalStore();
  const { t } = useTranslation();
  const { data, isLoading } = useGetAvailableBanksQuery();

  const [selectedBank, setSelectedBank] = useState<number | undefined>(
    preselectedBankId,
  );

  const { mutateAsync, isPending } = useInitiateConsentMutation();

  const { toastApiError } = useApiErrorUtils();
  const { handleBudgetError } = useBudgetFunctionalityDisabledError();

  const schema = useAddAccountSchema();

  const { control, handleSubmit, formState } = useForm<TAddAccountModalSchema>({
    defaultValues: {
      agreedToBudgetRules: false,
    },
    resolver: zodResolver(schema),
  });

  const { isValid } = formState;

  const onSubmit: SubmitHandler<TAddAccountModalSchema> = async (data) => {
    if (!selectedBank) return;

    const response = await mutateAsync({
      bankId: selectedBank,
      agreedToBudgetRules: data.agreedToBudgetRules,
    }).catch(handleBudgetError(toastApiError));

    if (response) {
      create((modalProps) => (
        <AccountScoringEmbedModal
          clientId={response.clientId}
          invitationKey={response.invitationKey}
          locale={response.locale}
          {...modalProps}
        />
      ));
      modal.close();
    }
  };

  const [, setSearchParams] = useSearchParams();

  // Reset search params when the modal opens
  // This is needed because Account Scoring embed wrongly parses callback url and breaks :|
  useEffect(() => {
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      modal={modal}
      title={t("budget.addAccount.modal.title")}
      subtitle={parseHtml(t("budget.addAccount.modal.subtitle"))}
      className="md:max-w-[32rem]"
      badgeProps={{ icon: PlusIcon, color: "green" }}
      actionButtons={
        <div className="flex w-full justify-between gap-3">
          <Button
            variant="secondary"
            className="w-full md:w-fit"
            onClick={modal.close}
          >
            {t("budget.addAccount.modal.cancel")}
          </Button>
          <Button
            className="w-full md:w-fit"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
            disabled={!selectedBank || !isValid}
          >
            {t("budget.addAccount.modal.submit")}
          </Button>
        </div>
      }
    >
      <div>
        <h5 className="pb-2 text-sm">{t("budget.addAccount.modal.bank")}</h5>
        <div className="flex flex-col flex-wrap gap-4 md:flex-row">
          {isLoading &&
            Array.from({ length: 7 }).map((_, index) => (
              <BankButtonSkeleton
                key={index}
                className="basis-[calc(50%-(1rem*2/3))]"
              />
            ))}
          {data?.banks.map((bank) => (
            <BankButton
              bankName={bank.bankName}
              isSelected={selectedBank === bank.bankId}
              onClick={() => setSelectedBank(bank.bankId)}
              className="basis-[calc(50%-(1rem*2/3))]"
            />
          ))}
        </div>
        <div className="mt-7">
          <FormCheckbox
            control={control}
            name="agreedToBudgetRules"
            label={
              <Trans
                i18nKey="budget.addAccount.modal.agreement"
                components={{
                  href: (
                    <Link
                      to={t("budget.addAccount.modal.agreementLink")}
                      target="_blank"
                    />
                  ),
                }}
              />
            }
          />
        </div>
        <ExpandingContainer
          buttonLabel={t(
            "budget.addAccount.modal.thirdPartyInformation.button",
          )}
          className="pt-4"
        >
          <div className="flex flex-col text-sm text-grey-dark">
            <span>
              {parseHtml(
                t("budget.addAccount.modal.thirdPartyInformation.title"),
              )}
            </span>
            <span>
              <Trans
                i18nKey="budget.addAccount.modal.thirdPartyInformation.email"
                components={{
                  href: (
                    <Link
                      to={t(
                        "budget.addAccount.modal.thirdPartyInformation.emailLink",
                      )}
                      target="_blank"
                    />
                  ),
                }}
              />
            </span>
            <span>
              {t("budget.addAccount.modal.thirdPartyInformation.phoneNumber")}
            </span>
            <span>
              {t("budget.addAccount.modal.thirdPartyInformation.address")}
            </span>
            <span>
              <Trans
                i18nKey="budget.addAccount.modal.thirdPartyInformation.website"
                components={{
                  href: (
                    <Link
                      to={t(
                        "budget.addAccount.modal.thirdPartyInformation.websiteLink",
                      )}
                      target="_blank"
                    />
                  ),
                }}
              />
            </span>
            <span>
              <Trans
                i18nKey="budget.addAccount.modal.thirdPartyInformation.otherInformation"
                components={{
                  terms: (
                    <Link
                      to={t(
                        "budget.addAccount.modal.thirdPartyInformation.termsAndConditionsLink",
                      )}
                      target="_blank"
                    />
                  ),
                  privacy: (
                    <Link
                      to={t(
                        "budget.addAccount.modal.thirdPartyInformation.privacyPolicyLink",
                      )}
                      target="_blank"
                    />
                  ),
                  complaint: (
                    <Link
                      to={t(
                        "budget.addAccount.modal.thirdPartyInformation.complaintHandlingLink",
                      )}
                      target="_blank"
                    />
                  ),
                }}
              />
            </span>
          </div>
        </ExpandingContainer>
      </div>
    </Modal>
  );
};
